import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import styled from "@emotion/styled";
import { useQuery } from "convex/react";
import { api } from "@/smart-views/client/api";
import { chatWithSmartView } from "@/smart-views/client/backend-api";
import {
  MDSComboboxVariant,
  MDSSidePanelHeader,
  MDSSidePanelHeaderSlotName,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  useTheme,
} from "@/mds";
import { MDSSession } from "@/mds/components/sessions/session";
import {
  IMDSSessionProps,
  IMDSSessionRef,
  SessionEvent,
  SessionEventKind,
  SessionFeature,
  SessionPartKind,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import { MDSComboboxDataSource, MDSComboboxItem } from "@/mds/components/forms";

interface SmartViewChatPanelProps {
  smartViewId: string;
  isClosed?: boolean;
  onOpen?: () => void;
}

export const SmartViewChatPanel: FC<SmartViewChatPanelProps> = ({
  smartViewId,
  isClosed = false,
  onOpen,
}) => {
  const data = useQuery(api.smartViews.client.getConversation, { smartViewId });
  const { theme } = useTheme();
  const sessionRef = useRef<IMDSSessionRef>(null);
  const visibleMessages = useMemo(
    () =>
      (data?.messages ?? []).filter(
        message => message.kind !== "interaction" && message.kind !== "comment"
      ),
    [data?.messages]
  );

  const sessionStatus = useMemo(() => {
    const lastMessage = visibleMessages.at(-1);
    if (!lastMessage) return SessionStatus.Initial;
    if (lastMessage.role === "agent") return SessionStatus.Finished;
    if (lastMessage.role === "system" && isTerminalSystemMessage(lastMessage.content)) {
      return SessionStatus.Finished;
    }
    return SessionStatus.Processing;
  }, [visibleMessages]);

  // The agent posts a stream of short narration messages while it's thinking
  // and tool-calling. We render those as ReasoningV1 parts so MDSSession's
  // built-in ReasoningGroup gives us: cycling latest line during processing,
  // auto-collapse when done, click-to-expand the full list. The final agent
  // communication message — the one substantive answer — renders outright as
  // a MarkdownV1 part. User messages and terminal/error system notifications
  // render as their own events with MarkdownV1 parts.
  const sessionEvents = useMemo<SessionEvent[]>(() => {
    const messages = visibleMessages;
    const isProcessing = sessionStatus === SessionStatus.Processing;
    const events: SessionEvent[] = [];

    // Walk messages, splitting into turns at each user message. Within each
    // turn, if the turn is finished and ends with an agent message, that last
    // agent message is the "real" response. Everything else non-user is a
    // thinking step.
    let i = 0;
    while (i < messages.length) {
      const message = messages[i];

      if (message.role === "user") {
        events.push(buildEvent(message, SessionEventKind.UserActionV1, SessionPartKind.MarkdownV1));
        i += 1;
        continue;
      }

      // Collect a run of non-user messages.
      const runStart = i;
      while (i < messages.length && messages[i].role !== "user") {
        i += 1;
      }
      const run = messages.slice(runStart, i);
      const isLastRun = i >= messages.length;
      const turnIsActive = isLastRun && isProcessing;

      // Decide which message (if any) is the "final" agent response. Only on
      // finished turns — during processing every agent narration is a step.
      let finalAgentIdx: number | null = null;
      if (!turnIsActive) {
        for (let j = run.length - 1; j >= 0; j--) {
          if (run[j].role === "agent") {
            finalAgentIdx = j;
            break;
          }
        }
      }

      run.forEach((stepMessage, idx) => {
        // Drop terminal system markers ("Done.") — the collapse itself signals
        // completion. Keep failure/limit messages so they're still visible.
        if (stepMessage.role === "system" && stepMessage.content === "Done.") {
          return;
        }

        if (idx === finalAgentIdx) {
          events.push(
            buildEvent(stepMessage, SessionEventKind.AgentPlanningV1, SessionPartKind.MarkdownV1)
          );
          return;
        }

        if (stepMessage.role === "system" && isTerminalSystemMessage(stepMessage.content)) {
          events.push(
            buildEvent(
              stepMessage,
              SessionEventKind.SystemNotificationV1,
              SessionPartKind.MarkdownV1
            )
          );
          return;
        }

        events.push(
          buildEvent(stepMessage, SessionEventKind.AgentPlanningV1, SessionPartKind.ReasoningV1)
        );
      });
    }

    return events;
  }, [sessionStatus, visibleMessages]);

  useEffect(() => {
    if (!data?.conversation) return;
    sessionRef.current?.setCurrentSessionId(data.conversation._id);
  }, [data?.conversation]);

  useEffect(() => {
    sessionRef.current?.upsertEvents(sessionEvents);
  }, [sessionEvents]);

  useEffect(() => {
    sessionRef.current?.updateStatus(sessionStatus);
  }, [sessionStatus]);

  const handleFetchComboboxData = useCallback(
    async (): Promise<MDSComboboxDataSource<MDSComboboxItem>> => ({ data: [] }),
    []
  );

  const handleInputFormSubmit = useCallback<IMDSSessionProps["handlers"]["handleInputFormSubmit"]>(
    async ({ content }) => {
      const trimmed = content.trim();
      if (!trimmed || !data?.conversation) {
        return false;
      }

      await chatWithSmartView({ smartViewId, message: trimmed });
      return true;
    },
    [data?.conversation, smartViewId]
  );

  const sessionProps: IMDSSessionProps = {
    sessionTitle: "Shape the smart view",
    sessionSubtitle: `Smart View: ${smartViewId}`,
    initialSessionEvents: sessionEvents,
    initialSessionStatus: sessionStatus,
    containerHeight: 400,
    isOpen: !isClosed,
    config: {
      features: [SessionFeature.AllowCopy],
      openLinksInNewTab: false,
      reasoningHeaderProcessingMinDisplayMs: 1000,
      linkHandler: () => {},
    },
    comboboxProps: {
      placeholder: "Tell this view what to change",
      variant: MDSComboboxVariant.Brand,
      chatIsClosed: isClosed,
      forceCollapse: isClosed,
      fetchIsInstant: true,
      mentions: [],
      onLeftAddonButtonClick: onOpen,
    },
    handlers: {
      handleFetchComboboxData,
      handleInputFormSubmit,
      handleNewSession: () => {},
      handleViewModeChanged: () => {},
      handleCountdownCompleted: () => {},
      handleRetry: () => {},
    },
  };

  return (
    <Container data-component="SmartViewChatPanel" $isInteractive={isClosed}>
      <PanelHeaderContainer $isClosed={isClosed} $isInteractive={isClosed}>
        <MDSSidePanelHeader
          backgroundColor={theme.colors.surface.chat}
          bottomBorderColor={theme.colors.border.onAccent}
        >
          <MDSText
            slot={MDSSidePanelHeaderSlotName.Heading}
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Contrast}
          >
            Shape the smart view
          </MDSText>
        </MDSSidePanelHeader>
      </PanelHeaderContainer>
      <SessionContainer>
        {data === null && !isClosed ? (
          <Unavailable>
            <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
              Smart View chat is unavailable.
            </MDSText>
          </Unavailable>
        ) : (
          <MDSSession ref={sessionRef} key={smartViewId} {...sessionProps} />
        )}
      </SessionContainer>
    </Container>
  );
};

const isTerminalSystemMessage = (content: string): boolean => {
  return content === "Done." || content.startsWith("Reached the iteration limit");
};

interface ConversationMessageLike {
  _id: string;
  conversationId: string;
  createdAt: number;
  content: string;
  role: "user" | "agent" | "system";
  kind?: "chat" | "comment" | "interaction";
}

const buildEvent = (
  message: ConversationMessageLike,
  eventKind: SessionEventKind,
  partKind: SessionPartKind.MarkdownV1 | SessionPartKind.ReasoningV1
): SessionEvent => {
  const createdAt = new Date(message.createdAt).toISOString();
  const part =
    partKind === SessionPartKind.ReasoningV1
      ? {
          id: `${message._id}-reasoning`,
          kind: SessionPartKind.ReasoningV1,
          summary: message.content,
        }
      : {
          id: `${message._id}-content`,
          kind: SessionPartKind.MarkdownV1,
          content: message.content,
        };
  return {
    id: message._id,
    session_id: message.conversationId,
    locally_created_at: createdAt,
    internal_created_at: createdAt,
    internal_updated_at: createdAt,
    feedbackGiven: null,
    data: {
      kind: eventKind,
      value: {
        parts: [part],
      },
    },
  } as SessionEvent;
};

const Container = styled.div<{ $isInteractive: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const PanelHeaderContainer = styled.div<{ $isClosed: boolean; $isInteractive: boolean }>`
  flex-shrink: 0;
  opacity: ${({ $isClosed }) => ($isClosed ? 0.9 : 1)};
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
`;

const SessionContainer = styled.div`
  flex: 1;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const Unavailable = styled.div`
  padding: ${({ theme }) => theme.spacing["5"]};
`;
